import { persistor, store } from "@/redux/store";

import "@fullcalendar/react/skeleton.css";
import "@fullcalendar/mui/monarch/theme.css";
import "@/styles/globals.css";

import type { AppProps } from "next/app";
import Head from "next/head";
import Script from "next/script";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { AppCacheProvider } from "@mui/material-nextjs/v16-pagesRouter";
import { Provider } from "react-redux";
import {
  Theme,
  ToastContainer,
  ToastContainerProps,
  ToastPosition,
} from "react-toastify";
import { PersistGate } from "redux-persist/integration/react";

import { baseLightTheme, className } from "@/theme/DefaultColors";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-quill-new/dist/quill.snow.css";
import "react-toastify/dist/ReactToastify.css";

import { LicenseInfo, muiXTelemetrySettings } from "@mui/x-license";

import ConfirmationDialog from "@/component/Confirmation";
import { SocketProvider } from "@/context";
import SupportAccessBanner from "@/component/support-access-banner";
import { FeatureFlagsProvider } from "@/utils/providers/feature-flags-provider";
import FeatureFlagsRouteGate from "@/utils/providers/feature-flags-route-gate";
import MapProvider from "@/utils/providers/maps-provider";
import { getMuiLicenseKey } from "@/utils/constants";
import PwaStatus from "@/component/pwa-status";
import PwaTitlebar from "@/component/pwa-titlebar";
import SessionTimeout from "@/component/session-timeout";

const MUI_LICENSE_KEY = getMuiLicenseKey();
muiXTelemetrySettings.disableTelemetry();
if (MUI_LICENSE_KEY) {
  LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);
}

const toastContainerProps: ToastContainerProps = {
  position: "top-center" as ToastPosition,
  autoClose: 3000,
  hideProgressBar: true,
  newestOnTop: true,
  closeOnClick: true,
  rtl: false,
  draggable: true,
  pauseOnHover: true,
  theme: "light" as Theme,
  stacked: true,
  style: {
    fontFamily: "Poppins",
  },
};

// Initialise Grafana Faro once on the client side.
// No-op when NEXT_PUBLIC_FARO_URL is not set (local dev).
if (typeof window !== "undefined") {
  import("@/runtime-utils/observability/faro").then(({ initialiseFaro }) =>
    initialiseFaro()
  );
}

// Direct import is safe — FaroProvider guards window usage internally
import FaroProvider from "@/component/FaroProvider";

export function reportWebVitals(metric: any) {
  if (window.gtag) {
    window.gtag("event", metric.name, {
      value: Math.round(
        metric.name === "CLS" ? metric.value * 1000 : metric.value
      ),
      event_label: metric.id,
      non_interaction: true,
    });
  }
}

export default function App({ Component, pageProps }: AppProps) {
  const theme = baseLightTheme;
  const gaId = process.env.NEXT_PUBLIC_GA_ID;

  return (
    <AppCacheProvider>
      <>
        <Head>
          <title>Wisterias Care Management System</title>
          <meta name="description" content="Care Manangement System" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="icon" href="/favicon.ico" />
        </Head>
        {gaId && (
          <>
            <Script
              strategy="afterInteractive"
              src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
            />
            <Script
              id="gtag-init"
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{
                __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${gaId}', {
                    page_path: window.location.pathname,
                  });
                `,
              }}
            />
          </>
        )}
        <main lang="en" className={className}>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                  <FeatureFlagsProvider>
                    <FeatureFlagsRouteGate>
                      <SocketProvider>
                        <MapProvider>
                          {/* FaroProvider adds error boundary + page-view tracking */}
                          <FaroProvider>
                            <Component {...pageProps} />
                            <SessionTimeout />
                            <PwaStatus />
                            <SupportAccessBanner />
                            <ConfirmationDialog />
                            <ToastContainer {...toastContainerProps} />
                          </FaroProvider>
                        </MapProvider>
                      </SocketProvider>
                    </FeatureFlagsRouteGate>
                  </FeatureFlagsProvider>
                </ThemeProvider>
              </StyledEngineProvider>
            </PersistGate>
          </Provider>
        </main>
      </>
    </AppCacheProvider>
  );
}
